import { useEffect, useRef, useState } from 'react';
import { DEMO_SHELF_URL } from './config.js';

// The size the shelf is rendered at inside the frame, then scaled down to fit
// whatever space the page has. A living-room display is a wide screen a long
// way from the sofa, and the app lays itself out for that; letting the iframe
// be 380px wide on a phone would show the responsive fallback instead of the
// thing being advertised. So the frame is always a 16:9 television, and the
// whole television gets smaller.
const FRAME_WIDTH = 1280;
const FRAME_HEIGHT = 720;

/**
 * A real share link, running, inside a television.
 *
 * Loaded late on purpose: the shelf fetches its own books and one cover image
 * per book, and none of that should compete with the top of the page. The
 * iframe has no `src` until it is nearly scrolled to.
 */
export default function ShelfDemo() {
  const frameRef = useRef(null);
  const [scale, setScale] = useState(null);
  const [live, setLive] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Fit the television to the column it has been given. ResizeObserver rather
  // than a resize listener, because this also has to react to the page's own
  // layout settling - fonts landing, the column changing width at a
  // breakpoint - which a window resize event never fires for.
  useEffect(() => {
    const node = frameRef.current;
    if (!node) return undefined;

    const fit = () => {
      const width = node.clientWidth;
      if (width > 0) setScale(width / FRAME_WIDTH);
    };

    fit();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', fit);
      return () => window.removeEventListener('resize', fit);
    }

    const observer = new ResizeObserver(fit);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Start loading a screen early, so it is already drawing by the time it is
  // actually in view rather than starting from blank under somebody's eyes.
  useEffect(() => {
    const node = frameRef.current;
    if (!node || !DEMO_SHELF_URL) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setLive(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setLive(true);
        observer.disconnect();
      },
      { rootMargin: '400px 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <figure className="tv">
      <div className="tv-bezel">
        <div className="tv-screen" ref={frameRef} style={{ aspectRatio: `${FRAME_WIDTH} / ${FRAME_HEIGHT}` }}>
          {/* Under the iframe, and never removed. It is what shows while the
              shelf is loading, and it is also what stays if the frame never
              arrives at all - a blocked third-party frame, an offline
              visitor - so the television is never an empty black hole. */}
          <div className={loaded ? 'tv-placeholder is-hidden' : 'tv-placeholder'} aria-hidden="true">
            <span className="tv-placeholder-glow" />
            <p>Warming up the shelf…</p>
          </div>

          {live && scale !== null && (
            <iframe
              className={loaded ? 'tv-frame is-loaded' : 'tv-frame'}
              title="A live Litbyt shelf, shared by its owner"
              src={DEMO_SHELF_URL}
              width={FRAME_WIDTH}
              height={FRAME_HEIGHT}
              style={{ transform: `scale(${scale})` }}
              loading="lazy"
              // It is a page from another origin in a box on ours. Scripts,
              // because the shelf is an app and draws nothing without them,
              // and same-origin, which here means the frame keeps its OWN
              // origin rather than being handed a null one - the shelf's
              // fetches to the API are CORS-checked against that name, and a
              // null origin is on no allowlist, so the television would sit
              // empty without it. It is still a different origin from this
              // page, so it grants the frame nothing of ours. What stays
              // withheld is what matters: no top-level navigation, no popups,
              // no forms, so an embedded shelf cannot steer the page around it.
              sandbox="allow-scripts allow-same-origin"
              // Decoration, not a control. Nobody should land on it by tabbing
              // towards the button underneath.
              tabIndex={-1}
              scrolling="no"
              onLoad={() => setLoaded(true)}
            />
          )}
        </div>
      </div>
      <div className="tv-stand" aria-hidden="true" />

      <figcaption className="tv-caption">
        A shelf somebody shared, loading live. Yours gets a link exactly like it.
        {DEMO_SHELF_URL && (
          <>
            {' '}
            <a className="text-link" href={DEMO_SHELF_URL} target="_blank" rel="noreferrer">
              Open it full screen
            </a>
          </>
        )}
      </figcaption>
    </figure>
  );
}
